import { Component, inject, OnInit, Injector, TemplateRef, ViewChild } from '@angular/core';
import { BreadcrumbService } from '@core/services/breadcrumb-service/BreadcrumbService';
import { TableBaseComponent } from '@shared/components/table-base/table-base';
import { MtxGridColumn, MtxGridRowClassFormatter } from '@ng-matero/extensions/grid';
import { SortDirection } from '@angular/material/sort';
import { FormlyFieldConfig } from '@ngx-formly/core';
import moment from 'moment';

import {
  catchError,
  of,
  tap,
} from 'rxjs';

import {
  BaseSearchOptions,
  FileResponse,
  InvoiceClient,
  InvoiceFilterOptions,
  InvoiceSearchOptions,
  InvoiceViewModel,
  OrderDirection,

} from '@core/api';

import { SessionStorageService } from '@core/services/session-storage-service/session-storage.service';
import { LocationFormlyFieldService } from '@core/services/formly-service/location-formly-field.service';
import { ModalFilterComponent } from '@shared/components/modal-filter/modal-filter.component';
import { ButtonProps } from '@affinis/smartus-components/lib/button/button.types';
import { Chip } from '@affinis/smartus-components/lib/chips/chips.component.types';
import { MenuItem } from '@affinis/smartus-components/lib/menu-extra/menu-extra.component';
import { ITableConfig} from '@shared/components/table-base/table-base.types';

const filterOptionsInvoiceContractsKey = 'filterOptionsInvoiceContracts';

@Component({
  selector: 'app-invoice-list',
  templateUrl: './invoice-list.component.html',
  styleUrls: [
    '../../../../../app/shared/components/table-base/table-base.scss',
    './invoice-list.component.scss'
  ],  
  standalone: false
})
export class InvoiceListComponent   extends TableBaseComponent 
  implements OnInit{

  private breadcrumbService = inject(BreadcrumbService);

  title: string;

  contractId: string;

  tenantName: string;

  contractName: string;

  modalComponent = ModalFilterComponent;

  loading = false;

  chips: Chip[] = [];

  menuItems: MenuItem[] = [];

  dialogData: any;

  override tableIdentifier = 'eInvoiceInvoiceList';

  override columns: MtxGridColumn[] = [];

  override filterFields: Array<FormlyFieldConfig> = [];

  @ViewChild('downloadInvoiceTemplate', { static: true })
  downloadInvoiceTemplateRef!: TemplateRef<void>;

  @ViewChild('newInvoiceTemplate', { static: true })
  newInvoiceTemplateRef!: TemplateRef<void>;

  constructor(
    injector: Injector,
    private readonly invoiceClient: InvoiceClient,
    private readonly sessionStorageService: SessionStorageService,
    private readonly locationFormlyFieldService: LocationFormlyFieldService
  ) {
    super(injector);

    this.config.grid.paging.pageSize = 10;
    this.query.pagingOptions.take = this.config.grid.paging.pageSize;
    this.query = {
      ...this.query,
      orderOptions: {
        attributeName: 'invoiceDate',
        orderDirection: OrderDirection.Desc,
      },
    };

    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras?.state) {
      this.contractId = navigation.extras.state.contractId;
      this.tenantName = navigation.extras.state.tenantName;
      this.contractName = navigation.extras.state.contractName;
    }
  }

  override config = this.configure(<ITableConfig>{
    title: 'title',
    translatePath: 'invoice.tenant_invoices',
  });  

  ngOnInit(): void {

    this.filterFields = this.getFilterFields();

    this.title = this.translate(this.config.title);

    this.breadcrumbService.setBreadcrumbs([{ label: this.title }]); 
    
    this.columns = [
      {
        header: this.translate('label__download'),
        field: 'action',
        cellTemplate: this.downloadInvoiceTemplateRef,
        width: '120px',
        resizable: true,
      },
      {
        header: this.translate('label__new'),
        cellTemplate: this.newInvoiceTemplateRef,
        field: 'newInvoice',
        sortable: true,
        class: '',
        resizable: true,
        minWidth: 30
      },
      {
        header: this.translate('label__createdAt'),
        field: 'invoiceDate',
        sortable: true,
        class: 'truncated',
        formatter: (data: any) =>
          data.invoiceDate
            ? moment(data.invoiceDate)
                .locale(this.translateService.instant('locale.locale'))
                .format(
                  this.translateService.instant(
                    'locale.moment__date_format'
                  )
                ) 
            : '--',
        resizable: true,
        width: '140px',
        minWidth: 134,
      },
      {
        header: this.translate('label__invoiceNumber'),
        field: 'externalId',
        sortable: true,
        class: '',
        resizable: true,
        minWidth: 30
      },
      {
        header: this.translate('label__textLine1'),
        field: 'invoiceText',
        sortable: true,
        class: 'truncated',
        resizable: true,
        minWidth: 50
      },
      {
        header: this.translate('label__object'),
        field: 'object',
        sortable: true,
        class: 'truncated',
        resizable: true,
        minWidth: 50
      }
    ];

    this.loadColumnVisibilityAndOrder();

    let filterOptions = this.sessionStorageService.get(filterOptionsInvoiceContractsKey);
    this.config.grid.filter.filterOptions = { ...filterOptions };

    this.mapFormModelToChips(filterOptions);

    this.dialogData = {
      columns: this.columns,
      filterFields: this.filterFields,
      filterValues: this.sessionStorageService.get(filterOptionsInvoiceContractsKey),
      actions: {
        onSubmit: (formModel: any) => {
          this.sessionStorageService.set(filterOptionsInvoiceContractsKey, formModel);

          filterOptions = this.sessionStorageService.get(filterOptionsInvoiceContractsKey);
          this.config.grid.filter.filterOptions = { ...filterOptions };

          this.mapFormModelToChips(formModel);
          this.updateFilter({
            ...formModel
          });

          this.refreshFromFilter();
          this.dialogData.filterValues =
            this.sessionStorageService.get(filterOptionsInvoiceContractsKey);
        },
        onReset: () => {
          this.sessionStorageService.set(filterOptionsInvoiceContractsKey, {});
          this.resetLocationSelection();

          filterOptions = this.sessionStorageService.get(filterOptionsInvoiceContractsKey);
          this.config.grid.filter.filterOptions = { ...filterOptions };

          this.mapFormModelToChips({});

          this.refreshFromFilter();
        },
      },
    };    
  }

  override getTableData$ = (
      query?: BaseSearchOptions,
      filterOptions?: InvoiceFilterOptions
      ) => {
 
      const searchOptions: InvoiceSearchOptions = {
        ...query,
        filterOptions,
        viewOptions: {
          contractId: this.contractId
        },
      };
  
      this.closeToast();
  
      return this.invoiceClient.getInvoices(searchOptions).pipe(
        tap(res => {
        }),
        catchError(() => {
  
          this.activeToast = this.toastr.error(
            this.translateService.instant(
              'crud.details.error.failed_to_load_msg'
            ),
            this.translateService.instant(
              'crud.details.error.failed_to_load_title'
            ),
            { timeOut: 0 }
          );
  
          return of([]);
        })
      );
    };

    controlButtons: ButtonProps[] = [
      {
        label: this.translateService.instant('crud.refresh'),
        onClick: () => this.refresh(),
      },
    ];

  onCloseDialog = (e: any) => {
    this.resetLocationSelection();
  };

  resetLocationSelection() {
    this.locationFormlyFieldService.resetSelection();
    this.dialogData.filterValues =
      this.sessionStorageService.get(filterOptionsInvoiceContractsKey);
  }

  getFilterFields() {
    return [
      {
        type: 'flex-layout',
        fieldGroup: [
          {
            type: 'input',
            key: 'externalId',
              props: {
              label: this.translate('label__invoiceNumber'),
            }
          },
          {
            type: 'input',
            key: 'object',
              props: {
              label: this.translate('label__object'),
            }
          },
          {
            type: 'input',
            key: 'InvoiceText',
              props: {
              label: this.translate('label__textLine1'),
            }
          },
          {
            type: 'checkbox',
            key: 'isNew',
              props: {
              label: this.translate('label__new'),
            }
          },
          {
            type: 'flex-layout-col',
            fieldGroup: [
              {
                type: 'datepicker',
                key: 'invoiceDateFrom',
                props: {
                  label: this.translate('label__invoiceDateFrom'),
                },
              },
              {
                type: 'datepicker',
                key: 'invoiceDateTo',
                props: {
                  label: this.translate('label__invoiceDateTo'),
                },
              },
            ],
          }
        ]
      }
    ];
  }

  downloadInvoice(event: MouseEvent, invoice: InvoiceViewModel) {
    event.stopPropagation();

    this.loading = true;


    this.invoiceClient.downloadInvoiceDocument(invoice.invoiceGuid).subscribe( {
      next: (response : FileResponse) => { 
        const blob = response.data!;
        const filename = response.fileName || 'invoice.pdf';

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();

        window.URL.revokeObjectURL(url);
        
        this.loading = false;

        this.invoiceClient.notifyPtbInvoiceReceived(invoice.invoiceGuid).subscribe({
          next: () => {
            this.getData();
          },
          error: () => {
            this.toastr.error(
            this.translateService.instant('generic.errorMessage')
          );
          },
        });
      },
        error: (err) => {
          this.loading = false;
          this.toastr.error(
            this.translateService.instant('generic.errorMessage')
          );
        }
      });

      return false;
  }

  handleOnRemoveChip = (eventObj: any) => {
    delete this.config.grid.filter.filterOptions[eventObj.id];

    this.sessionStorageService.set(filterOptionsInvoiceContractsKey, {
      ...this.config.grid.filter.filterOptions,
      ticketClassList: this.config.grid.filter.filterOptions.ticketClassList
        ? this.sessionStorageService.get(filterOptionsInvoiceContractsKey).ticketClassList
        : null,
    });

    this.dialogData.filterValues =
      this.sessionStorageService.get(filterOptionsInvoiceContractsKey);

    this.chips = this.chips.filter(chipItem => chipItem.id !== eventObj.id);
    this.refreshFromFilter();
  };

  mapFormModelToChips = formModel => {
    this.chips = [];
    this.chips.length = 0;

    Object.keys(formModel).forEach(formModelItemKey => {
      const flattened = this.filterFields[0].fieldGroup?.flatMap(field =>
        field.fieldGroup ? [field, ...field.fieldGroup] : field
      );
      const field = flattened?.find(fieldItem => {
        return fieldItem.key === formModelItemKey;
      });

      if (formModelItemKey === 'isNew') {
          let isNew: boolean | undefined = formModel[formModelItemKey];
          if (isNew !== undefined) {
            this.chips.push({
              id: formModelItemKey,
              label: isNew ? this.translate('label__invoiceUnread') : this.translate('label__invoiceRead'),
            });
          }
      }
      else if (formModelItemKey === 'invoiceDateTo' || formModelItemKey === 'invoiceDateFrom') 
        {
          const date = new Date(formModel[formModelItemKey]).toLocaleDateString(
            'de-DE'
          );
          this.chips.push({
            id: formModelItemKey,
          label: `${field.props.label}: ${date}`,
          });
        } 
        else if (formModel[formModelItemKey]) {
          this.chips.push({
            id: formModelItemKey,
            label: `${field.props.label}: ${formModel[formModelItemKey]}`,
          });
        }
      });
    }; 

    isFilterActive() : boolean {
      const filterOptions = this.config.grid.filter.filterOptions;
      return filterOptions && Object.keys(filterOptions).length > 0;
    }

    get noResultText(): string {
      if (this.isFilterActive()) {
        return this.translateService.instant('invoice.tenant_invoices.no_result_text_filtered');
      }
      return this.translateService.instant('invoice.tenant_invoices.no_result_text');
    }
  }
